import { CHARGE_REST, HERALD_HOVER, TENDRILS } from '../../models/storm-herald.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Heraldo de la tormenta (models/storm-herald.ts): a squall with a hood. It hangs at its hover height and never
 * touches the ground until it dies: it bobs and rolls like a cloud on the wind, its tendrils trailing and curling a
 * beat behind every move, its arms drifting.
 *
 * Conventions: +X on the body or the hood pitches it forward; on an arm −X swings it forward and up, +Z lifts the
 * right arm (+X) out to the side and −Z the left; −X on a forearm bends the elbow. +X on a tendril swings its tip
 * back (it trails as the cloud drifts forward). `charge` (a ball of lightning over its head) and `bolt` (a fork
 * thrown down and forward) are built tiny and only the attack and cast clips scale them up (`1 / CHARGE_REST`).
 *
 * Blows: attack (a fork from its right claw) strikes at 0.13 s and the bolt shows 0.1–0.3 s; cast (it calls the
 * lightning down on a tower) gathers the ball overhead from 0.3 s, and throws the bolt at 1.1 s (shows to 1.35 s).
 */

type T = `t${(typeof TENDRILS)[number]}${1 | 2}`;
type B = 'root' | 'body' | 'hood' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'charge' | 'bolt' | T;
type P = KeyPose<B>;

const FULL = 1 / CHARGE_REST;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Each tendril sways on its own phase, its lower piece swinging twice as far and a beat later; `trail` sweeps them all back. */
const tendrils = (b: Bones<B>, t: number, amp: number, trail: number, freq = 0.5): void => {
  TENDRILS.forEach((n, i) => {
    const o = i * 1.3;
    const w = TAU * freq * t;
    b[`t${n}1`].rot(trail * 0.6 + amp * Math.sin(w + o), 0, amp * 0.8 * Math.cos(w * 0.8 + o));
    b[`t${n}2`].rot(trail * 0.5 + amp * 1.8 * Math.sin(w + o - 0.9), 0, amp * 1.4 * Math.cos(w * 0.8 + o - 0.9));
  });
};

/** A strobe for lightning: mostly on, with dropouts (deterministic in `t`). */
const strobe = (t: number): number => (Math.sin(t * 57) > -0.55 ? 1 : 0.35);

const hover = (dur: number, name: string, trail: number, lean: number): Clip<B> =>
  clip<B>(name, dur, true, (b, t) => {
    const p = phase(t, dur) * TAU;
    b.root.pos(0, 0.06 * Math.sin(p), 0.02 * Math.sin(p * 2)).rot(0.03 * Math.sin(p + 0.6) + lean * 0.3, 0, 0.035 * Math.sin(p + 1.2));
    b.body.rot(lean + 0.04 * Math.sin(p * 2 + 0.4), 0.06 * Math.sin(p), 0.03 * Math.cos(p)).scale(1 + 0.025 * Math.sin(p * 2));
    b.hood.rot(-lean * 0.6 + 0.06 * Math.sin(p + 1.4), 0.25 * Math.sin(p * 0.5 + 0.8) * (trail > 0 ? 0.3 : 1), 0.04 * Math.sin(p + 2));
    b.armL.rot(0.08 * Math.sin(p + 0.5) + trail * 0.5, 0, -0.06 * Math.sin(p + 1.1));
    b.armR.rot(0.08 * Math.sin(p + 1.9) + trail * 0.5, 0, 0.06 * Math.sin(p + 2.5));
    b.foreL.rot(-0.1 * Math.sin(p + 1.4) - 0.1);
    b.foreR.rot(-0.1 * Math.sin(p + 2.8) - 0.1);
    tendrils(b, t, 0.12, trail, 2 / dur);
  }, (t) => {
    const s = Math.sin(phase(t, dur) * TAU * 2);
    return [1 + 0.015 * s, 1 - 0.012 * s, 1 + 0.015 * s];
  });

/** A fork from the right claw: it rears, flings the arm down and forward, and the bolt cracks out of it. */
const attack = (): Clip<B> =>
  clip<B>('attack', 1.2, false, (b, t) => {
    const cocked: P = { root: [0, 0, 0, 0, 0.08, -0.05], body: [-0.2, 0.25, 0], hood: [-0.2, -0.2, 0], armR: [-2.2, 0, 0.7], foreR: [-0.6, 0, 0], armL: [0.3, 0, -0.4] };
    const flung: P = { root: [0, 0, 0, 0, -0.03, 0.08], body: [0.3, -0.2, 0], hood: [0.25, 0.1, 0], armR: [-0.9, 0, -0.1], foreR: [0.2, 0, 0], armL: [0.4, 0, -0.5], foreL: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, cocked],
      [0.05, { ...cocked, armR: [-2.35, 0, 0.75] }, outQuad],
      [0.13, flung, inCubic],
      [0.45, { ...flung, body: [0.26, -0.18, 0] }, outCubic],
      [1.2, {}, inOut],
    ], { foreR: 0.03, hood: 0.04 });
    tendrils(b, t, 0.1, 0.4 * envelope(t, 0, 0.13, 0.3, 0.9), 0.8);
    const on = envelope(t, 0.1, 0.13, 0.24, 0.32) * strobe(t);
    b.bolt.scale(1 + (FULL - 1) * on);
    b.body.scale(1 + 0.05 * kick(t, 0.13, 5, 8));
  });

/**
 * Cast: it rises, lifts both arms over its hood and a ball of lightning gathers between its claws (0.3–1.0 s),
 * the hood thrown back; then it hurls it down (1.1 s): the ball bursts, the fork cracks to the ground ahead, the
 * cloud heaves with the recoil and sinks back to its hover.
 */
const cast = (): Clip<B> =>
  clip<B>('cast', 2.1, false, (b, t) => {
    const raise: P = { root: [0, 0, 0, 0, 0.22, -0.04], body: [-0.18, 0, 0, 0, 0, 0, 1.06], hood: [-0.45, 0, 0], armL: [-2.6, 0, -0.55], foreL: [-0.5, 0, 0], armR: [-2.6, 0, 0.55], foreR: [-0.5, 0, 0] };
    const hurl: P = { root: [0, 0, 0, 0, 0.05, 0.1], body: [0.4, 0, 0, 0, 0, 0, 0.96], hood: [0.35, 0, 0], armL: [-0.75, 0, -0.15], foreL: [0.1, 0, 0], armR: [-0.75, 0, 0.15], foreR: [0.1, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.45, raise, inOut],
      [0.98, { ...raise, root: [0, 0, 0, 0, 0.26, -0.05], armL: [-2.75, 0, -0.5], armR: [-2.75, 0, 0.5] }],
      [1.1, hurl, inCubic],
      [1.4, { ...hurl, body: [0.32, 0, 0] }, outBack],
      [2.1, {}, inOut],
    ], { hood: 0.05, foreL: 0.03, foreR: 0.03 });
    tendrils(b, t, 0.14, -0.3 * envelope(t, 0.3, 0.6, 1.0, 1.1) + 0.5 * envelope(t, 1.1, 1.15, 1.4, 1.9), 0.9);
    const ball = span(t, 0.3, 0.95, outCubic) * (t < 1.08 ? 1 : 0) * (0.85 + 0.15 * Math.sin(t * 43));
    b.charge.scale(1 + (FULL - 1) * ball);
    const fork = envelope(t, 1.06, 1.1, 1.28, 1.38) * strobe(t);
    b.bolt.scale(1 + (FULL - 1) * fork);
    b.root.pos(0, 0.04 * kick(t, 1.1, 4, 7), 0);
  }, (t) => {
    const k = Math.max(0, kick(t, 1.1, 3, 8));
    return [1 + 0.05 * k, 1 - 0.07 * k, 1 + 0.05 * k];
  }, 'Llamar al rayo');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.root.pos(0, 0.04 * q, -0.06 * q);
    b.body.rot(-0.15 * q, 0.1 * q, 0).scale(1 - 0.06 * q);
    b.hood.rot(-0.25 * q, -0.1 * q, 0.1 * q);
    b.armL.rot(0.3 * q, 0, -0.2 * q);
    b.armR.rot(0.3 * q, 0, 0.2 * q);
    tendrils(b, t, 0.05, -0.4 * q);
  });

/** Stunned: it sags in the air and turns slowly, the arms hanging, the tendrils limp. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    b.root.pos(0, -0.12 + 0.03 * Math.sin(p), 0).rot(0.05 * Math.sin(p), 0.3 * Math.sin(p * 0.5), 0.08 * Math.cos(p));
    b.body.rot(0.25, 0, 0).scale(0.97);
    b.hood.rot(0.5 + 0.1 * Math.sin(p + 0.8), 0.25 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.armL.rot(0.3, 0, 0.2);
    b.armR.rot(0.3, 0, -0.2);
    b.foreL.rot(0.2);
    b.foreR.rot(0.2);
    tendrils(b, t, 0.04, 0, 0.4);
  });

/**
 * The death (2.4 s): a last flash through it, the cloud convulses, then it loses its hold on the air: it drops
 * (1.2 s on the ground), slumps and spreads into a low heap of dead cloud.
 */
const die = (): Clip<B> =>
  clip<B>('die', 2.4, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.2, { root: [0, 0, 0, 0, 0.12, 0], body: [-0.35, 0, 0, 0, 0, 0, 1.12], hood: [-0.7, 0, 0], armL: [-2.2, 0, -0.9], foreL: [0.2, 0, 0], armR: [-2.2, 0, 0.9], foreR: [0.2, 0, 0] }, outExpo],
      [0.55, { root: [0, 0, 0, 0, 0.05, 0], body: [0.1, 0.2, 0.1, 0, 0, 0, 0.95], hood: [0.4, 0.3, 0.2], armL: [0.2, 0, 0.4], armR: [0.3, 0, -0.5] }, inOut],
      [1.2, { root: [0.1, 0, 0.12, 0, -HERALD_HOVER + 0.32, 0.05], body: [0.5, 0.3, 0.2, 0, 0, 0, 0.82], hood: [0.7, 0.3, 0.3], armL: [0.6, 0, 0.6], foreL: [0.4, 0, 0], armR: [0.7, 0, -0.7], foreR: [0.4, 0, 0] }, inQuad],
      [2.4, { root: [0.12, 0, 0.14, 0, -HERALD_HOVER + 0.24, 0.05], body: [0.6, 0.35, 0.25, 0, 0, 0, 0.7], hood: [0.9, 0.3, 0.4], armL: [0.8, 0, 0.9], foreL: [0.5, 0, 0], armR: [0.9, 0, -0.9], foreR: [0.5, 0, 0] }, outCubic],
    ], { hood: 0.05 });
    // The tendrils whip in the convulsion, then splay limp on the ground.
    const limp = span(t, 1.0, 1.6);
    tendrils(b, t, 0.25 * (1 - limp), -0.6 * envelope(t, 0.1, 0.25, 0.5, 0.9) + 1.0 * limp, 2);
    const flash = envelope(t, 0.02, 0.06, 0.16, 0.22) * strobe(t);
    b.bolt.scale(1 + (FULL - 1) * flash);
  }, (t) => {
    const s = span(t, 1.1, 2.2, outCubic);
    const k = Math.max(0, kick(t, 1.2, 3, 8));
    return [1 + 0.35 * s + 0.05 * k, 1 - 0.45 * s - 0.06 * k, 1 + 0.35 * s + 0.05 * k];
  });

/** Clips by library kind (see ../rigs.ts). */
export const stormHeraldClips = (): Clip[] => [hover(3.2, 'idle', 0, 0), hover(2.4, 'walk', 0.45, 0.18), attack(), hit(), stun(), die(), cast()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { stormHerald: stormHeraldClips };

